/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Row } from "react-bootstrap";
import { api, ApiError } from "core/api";
import { Button, Card, Input, FieldGroup, Form, Switch } from "core/components";
import { ChangeOrderSchema, ProjectFormData } from "core/schema";
import { admin_change_orders_path, admin_change_order_path, admin_inventory_items_path, admin_tasks_path } from "routes";
import { Controller, FormProvider, useForm } from "react-hook-form";
import ProjectChangeOrderContext from "./ProjectChangeOrderContext";
import ChangeOrderTasksForm from "./ChangeOrderTasksForm";
import DocumentFields from "./DocumentFields";
import { yupResolver } from "@hookform/resolvers/yup";

type ProjectChangeOrderProps = {
  project: ProjectFormData;
};

const ProjectChangeOrder = ({
  changeOrder,
  project,
  showNewChangeOrder,
  setShowNewChangeOrder,
  tasks,
  items,
  is_admin_or_architect,
  changeOrderTasks,
  isItemsLoading
}: ProjectChangeOrderProps): JSX.Element => {
  const formMethods = useForm({
    mode: "onSubmit",
    defaultValues: {
      ...changeOrder
    },
    resolver: yupResolver(ChangeOrderSchema)
  });
  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting, isValidating, errors }
  } = formMethods;
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const [tasksForDestruction, setTasksForDestruction] = React.useState<Array>([]);
  const [itemsForDestruction, setItemsForDestruction] = React.useState<Array>([]);
  const [processing, setProcessing] = React.useState(false);

  const withTasksForDestruction = (data) => {
    const { change_order_tasks_attributes } = data;

    for (let index = 0; index < change_order_tasks_attributes.length; index++) {
      const changeOrderTask = change_order_tasks_attributes[index];
      const { change_order_task_items_attributes } = changeOrderTask;
      const changeOrderTaskItems = itemsForDestruction.filter(
        (changeOrderTaskItem) => changeOrderTaskItem.change_order_task_id == changeOrderTask.id
      );
      change_order_tasks_attributes[index] = {
        ...changeOrderTask,
        change_order_task_items_attributes: [...change_order_task_items_attributes, ...changeOrderTaskItems]
      };
    }

    return { ...data, change_order_tasks_attributes: [...change_order_tasks_attributes, ...tasksForDestruction] };
  };

  const save = async (data): Promise<unknown> => {
    setFormErrors([]);
    setProcessing(true);

    let filteredData = {};
    if (changeOrder.change_order_type === "replace") {
      filteredData = {
        ...data,
        active_project_tasks: changeOrderTasks
      };
    }

    try {
      if (data.id) {
        changeOrder.change_order_type === "replace"
          ? await api.put(admin_change_order_path(data.id), withTasksForDestruction(filteredData))
          : await api.put(admin_change_order_path(data.id), withTasksForDestruction(data));
      } else {
        changeOrder.change_order_type === "replace"
          ? await api.post(admin_change_orders_path(), filteredData)
          : await api.post(admin_change_orders_path(), withTasksForDestruction(data));
      }
      setShowNewChangeOrder(!showNewChangeOrder);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setProcessing(false);
    }
  };

  const onError = (data) => {
    console.error(data);

    if (data.change_order_tasks_attributes?.type) {
      setFormErrors([data.change_order_tasks_attributes?.message]);
    } else {
      setFormErrors(["There are errors on your form."]);
    }
  };

  const content = () => {
    return (
      <ProjectChangeOrderContext.Provider value={{ project, setTasksForDestruction, setItemsForDestruction }}>
        <FormProvider {...formMethods}>
          <Form errors={formErrors}>
            <Row>
              <Col>
                <Controller
                  name="include_budget"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      {...field}
                      label="Include Budget"
                      info="Include or exclude change order to the budget amounts in the Actual vs Budget Report and Performance Evaluation Report"
                      checked={field.value}
                      value={field.value?.toString()}
                      errorMessage={errors.include_budget?.message}
                    />
                  )}
                />
                <Input {...register(`code`)} label="Change Order ID" readOnly={true} />
                <DocumentFields readOnly={changeOrder?.id} />
              </Col>
              <Col>
                <Controller
                  name={`description`}
                  control={control}
                  render={({ field }) => (
                    <FieldGroup label="Description" errorMessage={errors?.description?.message}>
                      <textarea {...field} className="field" placeholder={"Change order description"} maxLength={255} value={field.value || ""} />
                    </FieldGroup>
                  )}
                />
              </Col>
            </Row>
            <Row className="mt-4 mx-1">
              <ChangeOrderTasksForm changeOrder={changeOrder} tasks={tasks} items={items} isItemsLoading={isItemsLoading} />
            </Row>
          </Form>
        </FormProvider>
      </ProjectChangeOrderContext.Provider>
    );
  };

  const actions = () => {
    return (
      <Row>
        <Col lg={12} className="d-flex justify-content-end">
          <Button
            className="button--default mr-2"
            icon="times"
            title="Cancel"
            type="button"
            onClick={() => {
              setShowNewChangeOrder(!showNewChangeOrder);
            }}
          />
          {is_admin_or_architect && (
            <Button
              className="button--brand"
              icon="save"
              title={`${changeOrder?.type == "add" ? "Submit Additional Change Order" : "Submit Replacement Change Order"}`}
              type="button"
              loading={isSubmitting || isValidating || processing}
              disabled={isSubmitting || isValidating || processing}
              onClick={handleSubmit(
                async (data) => save(data),
                (data) => onError(data)
              )}
            />
          )}
        </Col>
      </Row>
    );
  };

  return (
    <React.StrictMode>
      {actions()}
      <Card.Body>{content()}</Card.Body>
    </React.StrictMode>
  );
};

export default ProjectChangeOrder;
